import { Button } from '@creditornot/cb-button';
import { FormRow, InputContainer } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useContext, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { bulkChangeCategory } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { BulkChangeCategoryPayload, Category, Menu } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { WoltModal } from 'common/components';
import { localize } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import { reportBulkAction } from 'modules/menu/behavioral';
import { MultiLevelSelect } from 'modules/menu/components/MultiLevelSelect';
import { WOLT_SPECIALS_CATEGORY_ID } from 'modules/menu/constants';
import { useBulkChangeCategoryTracking } from 'modules/menu/hooks/telemetryHooks';
import useMenuData from 'modules/menu/useMenuData';

import { buildBulkActionBasePayload } from '../../utils';
import BulkActionModalTitle from '../BulkActionModalTitle';

type Props = {
  categories: Category[];
  menuId: string;
  onClearSelection: () => void;
  onClose: () => void;
  selectedIds: Set<string>;
};

const Root = styled(WoltModal)`
  width: calc(100vw - 40px);
  min-width: 320px;
  max-width: 640px;
  padding: 0;
  overflow-y: visible;
`;

const StyledModalHeader = styled(ModalHeader)`
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const StyledModalFooter = styled(ModalFooter)`
  background-color: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const CancelButton = styled(Button).attrs({ variant: 'lightBlue', size: 'small' })`
  margin-inline-end: 16px;
`;
const SubmitButton = styled(Button).attrs({ variant: 'blue', size: 'small' })``;

const createCategoryOption = (category: Category, parentId: null | string, primaryLanguage: string) => ({
  value: category.id,
  label: localize(category.name, primaryLanguage),
  parentId,
  childrenIds: category.child_categories.map(subcategory => subcategory.id),
});

const BulkCategoryChangeModal = ({ categories, menuId, onClearSelection, onClose, selectedIds }: Props) => {
  const intl = useIntl();
  const { updateMenuData } = useMenuData();

  const { primaryLanguage } = useContext(MenuLocaleContext);

  const [destinationCategoryId, setDestinationCategoryId] = useState<string | null>(null);
  const trackChangeCategory = useBulkChangeCategoryTracking([...selectedIds], destinationCategoryId ?? '');

  const handleBulkCategoryChange = useMutation(
    () => {
      if (!destinationCategoryId) {
        return Promise.reject();
      }

      const payload: BulkChangeCategoryPayload = {
        ...buildBulkActionBasePayload(categories, selectedIds),
        destination_category_id: {
          $oid: destinationCategoryId,
        },
      };

      return bulkChangeCategory(menuId, payload);
    },
    {
      onMutate: () => {
        reportBulkAction(selectedIds.size, 'BULK_CHANGE_ITEM_CATEGORY', { categoryChangeMethod: 'modal' });
      },
      onSuccess: (data: Menu) => {
        updateMenuData({ actionType: 'updateMenu', payload: data });
        onClearSelection();
        trackChangeCategory();
        toast.success(<FormattedMessage id="menu.bulkActions.categoryChange.successDescription" />);
      },
      onError: () => {
        toast.error(<FormattedMessage id="menu.bulkActions.categoryChange.errorDescription" />, {
          duration: Infinity,
        });
      },
      onSettled: () => {
        onClose();
      },
    },
  );

  const categoryOptions = useMemo(
    () =>
      categories
        .filter(category => category.id !== WOLT_SPECIALS_CATEGORY_ID)
        .flatMap(category => [
          createCategoryOption(category, null, primaryLanguage),
          ...category.child_categories.map(subcategory =>
            createCategoryOption(subcategory, category.id, primaryLanguage),
          ),
        ]),
    [categories, primaryLanguage],
  );

  return (
    <Root isOpen onRequestClose={onClose}>
      <StyledModalHeader onCloseClick={onClose}>
        <BulkActionModalTitle
          count={selectedIds.size}
          title={<FormattedMessage id="menu.bulkActions.categoryChange.title" />}
        />
      </StyledModalHeader>

      <StyledModalContent>
        <FormRow label={intl.formatMessage({ id: 'menu-item.category' })} labelAlign="center" horizontalPadding>
          <InputContainer>
            <MultiLevelSelect
              caretPosition="right"
              value={destinationCategoryId}
              onChange={setDestinationCategoryId}
              options={categoryOptions}
              placeholder={intl.formatMessage({ id: 'menu.selectOrSearch' })}
            />
          </InputContainer>
        </FormRow>
      </StyledModalContent>
      <StyledModalFooter>
        <CancelButton onClick={onClose} disabled={handleBulkCategoryChange.isLoading}>
          <FormattedMessage id="state.cancel" />
        </CancelButton>
        <SubmitButton
          disabled={!destinationCategoryId || handleBulkCategoryChange.isLoading}
          loading={handleBulkCategoryChange.isLoading}
          type="submit"
          onClick={() => handleBulkCategoryChange.mutate()}
        >
          <FormattedMessage id="state.save" />
        </SubmitButton>
      </StyledModalFooter>
    </Root>
  );
};

export default BulkCategoryChangeModal;
